import type { FormEventHandler } from 'react'
import { Controller, type Control, useWatch } from 'react-hook-form'
import AddIcon from '@mui/icons-material/Add'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Checkbox from '@mui/joy/Checkbox'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'
import type { CountryCode } from 'libphonenumber-js'

import FloatingLabelCountryAutocomplete from '@/components/checkout/floating-label-country-autocomplete'
import FloatingLabelInput from '@/components/checkout/floating-label-input'
import FloatingLabelPhoneInput from '@/components/checkout/floating-label-phone-input'
import type { EmailDomainSuggestion } from '@/components/checkout/contact-info/email-domain-suggestion'
import {
    getCheckoutValidationMessages,
    getContactValidationRule,
    validateContactPhone,
    type CheckoutFormValues,
} from '@/components/checkout/page/controller'

// Every checkout Save/Pay control must carry this marker. It prevents blur-time
// suggestion rendering from moving the clicked control before its submit handler runs.
const CHECKOUT_SUBMISSION_SELECTOR = '[data-checkout-submission]'

function isCheckoutSubmissionTarget(target: EventTarget | null): boolean {
    return target instanceof Element
        && target.closest(CHECKOUT_SUBMISSION_SELECTOR) !== null
}

function getFieldErrorMessage(error?: { message?: string }): string | null {
    return typeof error?.message === 'string' ? error.message : null
}

function ValidationError({ message }: { message: string | null }) {
    if (!message) {
        return null
    }

    return (
        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
            {message}
        </Typography>
    )
}

type CheckoutContactInfoFormProps = {
    control: Control<CheckoutFormValues>
    invoiceRequired: boolean
    showPhoneNumber2: boolean
    defaultPhoneCountry: CountryCode
    hideActions?: boolean
    requestInProgress?: boolean
    errorMessage?: string | null
    emailDomainSuggestion: EmailDomainSuggestion | null
    onCancel: () => void
    onClear: () => void
    onCheckEmailDomainSuggestion: (email: string) => void
    onClearEmailDomainSuggestion: () => void
    onShowPhoneNumber2: () => void
    onPhoneCountryChange: (country: CountryCode) => void
    onSubmit: FormEventHandler<HTMLFormElement>
}

export default function CheckoutContactInfoForm({
    control,
    invoiceRequired,
    showPhoneNumber2,
    defaultPhoneCountry,
    hideActions = false,
    requestInProgress = false,
    errorMessage,
    emailDomainSuggestion,
    onCancel,
    onClear,
    onCheckEmailDomainSuggestion,
    onClearEmailDomainSuggestion,
    onShowPhoneNumber2,
    onPhoneCountryChange,
    onSubmit,
}: CheckoutContactInfoFormProps) {
    const { t } = useTranslation()
    const validationMessages = getCheckoutValidationMessages(t)
    const requestedInvoice = useWatch({
        control,
        name: 'requested_invoice',
    })

    return (
        <form onSubmit={onSubmit} style={{ marginBottom: 0 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pt: 1.5,
            }}>
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        validate: getContactValidationRule('name', requestedInvoice, invoiceRequired, validationMessages),
                    }}
                    render={({ field, fieldState }) => (
                        <Box>
                            <FloatingLabelInput
                                {...field}
                                label={t('checkout.contactInfo.name')}
                                required
                                error={!!fieldState.error}
                            />
                            <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                        </Box>
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        validate: getContactValidationRule('email', requestedInvoice, invoiceRequired, validationMessages),
                    }}
                    render={({ field, fieldState }) => {
                        const suggestion = emailDomainSuggestion?.source === field.value
                            ? emailDomainSuggestion.correctedEmail
                            : null

                        return (
                            <Box>
                                <FloatingLabelInput
                                    {...field}
                                    label={t('checkout.contactInfo.email')}
                                    type="email"
                                    required
                                    error={!!fieldState.error}
                                    onChange={event => {
                                        onClearEmailDomainSuggestion()
                                        field.onChange(event)
                                    }}
                                    onBlur={event => {
                                        field.onBlur()

                                        if (!isCheckoutSubmissionTarget(event.relatedTarget)) {
                                            onCheckEmailDomainSuggestion(event.target.value)
                                        }
                                    }}
                                />
                                <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                {suggestion ? (
                                    <Button
                                        type="button"
                                        variant="plain"
                                        color="danger"
                                        size="sm"
                                        aria-live="polite"
                                        onClick={() => {
                                            field.onChange(suggestion)
                                            onClearEmailDomainSuggestion()
                                        }}
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            px: 0.5,
                                            py: 1,
                                            minHeight: 'auto',
                                            fontSize: 'var(--joy-fontSize-xs)',
                                            fontWeight: 400,
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                color: 'var(--joy-palette-danger-500)',
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {t('checkout.contactInfo.emailSuggestion', { email: suggestion })}
                                    </Button>
                                ) : null}
                            </Box>
                        )
                    }}
                />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Controller
                        name="phone_number"
                        control={control}
                        rules={{
                            validate: value => validateContactPhone(
                                value,
                                defaultPhoneCountry,
                                true,
                                validationMessages,
                            ),
                        }}
                        render={({ field, fieldState }) => (
                            <Box>
                                <FloatingLabelPhoneInput
                                    label={t('checkout.contactInfo.phoneNumber1')}
                                    name={field.name}
                                    inputRef={field.ref}
                                    value={field.value}
                                    defaultCountry={defaultPhoneCountry}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    onCountryChange={onPhoneCountryChange}
                                    required
                                    error={!!fieldState.error}
                                />
                                <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                            </Box>
                        )}
                    />
                    {showPhoneNumber2 &&
                        <Box pt={2}>
                            <Controller
                                name="phone_number_2"
                                control={control}
                                rules={{
                                    validate: value => validateContactPhone(
                                        value,
                                        defaultPhoneCountry,
                                        false,
                                        validationMessages,
                                    ),
                                }}
                                render={({ field, fieldState }) => (
                                    <Box>
                                        <FloatingLabelPhoneInput
                                            label={t('checkout.contactInfo.phoneNumber2')}
                                            name={field.name}
                                            inputRef={field.ref}
                                            value={field.value}
                                            defaultCountry={defaultPhoneCountry}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            onCountryChange={onPhoneCountryChange}
                                            error={!!fieldState.error}
                                        />
                                        <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                    </Box>
                                )}
                            />
                        </Box>
                    }
                    <Typography level="body-xs" sx={{ mt: 0.5 }}>
                        {t('checkout.contactInfo.additionalPhoneInfo')}
                    </Typography>
                </Box>
                {!showPhoneNumber2 &&
                    <Button
                        type="button"
                        variant="plain"
                        startDecorator={<AddIcon />}
                        onClick={onShowPhoneNumber2}
                        sx={{
                            alignSelf: 'flex-start',
                            px: 1,
                            minHeight: 'auto',
                            fontWeight: 600,
                            '&& svg': {
                                fill: 'currentColor',
                            },
                            '&:hover': {
                                color: '#484848',
                            },
                            '&&:hover svg': {
                                fill: 'currentColor',
                            },
                        }}
                    >
                        {t('checkout.contactInfo.addPhoneNumber')}
                    </Button>
                }
                {!invoiceRequired ? (
                    <Controller
                        name="requested_invoice"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                label={t('checkout.contactInfo.invoiceRequest')}
                                checked={field.value}
                                onChange={event => field.onChange(event.target.checked)}
                                sx={{
                                    alignItems: 'center',
                                    mt: 1,
                                    '& .MuiCheckbox-label': {
                                        fontSize: '14px',
                                    },
                                }}
                            />
                        )}
                    />
                ) : null}
                {requestedInvoice ? (
                    <>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            pt: 1,
                        }}>
                            <ReceiptLongIcon
                                sx={{
                                    color: '#484848',
                                    fontSize: '18px',
                                }}
                            />
                            <Typography level="body-md" sx={{ fontWeight: 600 }}>
                                {t('checkout.contactInfo.invoiceHeading')}
                            </Typography>
                        </Box>
                        <Controller
                            name="invoice_title"
                            control={control}
                            rules={{
                                validate: getContactValidationRule('invoice_title', requestedInvoice, invoiceRequired, validationMessages),
                            }}
                            render={({ field, fieldState }) => (
                                <Box>
                                    <FloatingLabelInput
                                        {...field}
                                        label={t(invoiceRequired
                                            ? 'checkout.contactInfo.mandatoryInvoiceTitle'
                                            : 'checkout.contactInfo.invoiceTitle')}
                                        required
                                        error={!!fieldState.error}
                                    />
                                    <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                </Box>
                            )}
                        />
                        <Controller
                            name="invoice_street"
                            control={control}
                            rules={{
                                validate: getContactValidationRule('invoice_street', requestedInvoice, invoiceRequired, validationMessages),
                            }}
                            render={({ field, fieldState }) => (
                                <Box>
                                    <FloatingLabelInput
                                        {...field}
                                        label={t('checkout.contactInfo.invoiceStreet')}
                                        required
                                        error={!!fieldState.error}
                                    />
                                    <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                </Box>
                            )}
                        />
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 1,
                        }}>
                            <Controller
                                name="invoice_city"
                                control={control}
                                rules={{
                                    validate: getContactValidationRule('invoice_city', requestedInvoice, invoiceRequired, validationMessages),
                                }}
                                render={({ field, fieldState }) => (
                                    <Box>
                                        <FloatingLabelInput
                                            {...field}
                                            label={t('checkout.contactInfo.invoiceCity')}
                                            required
                                            error={!!fieldState.error}
                                        />
                                        <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                    </Box>
                                )}
                            />
                            <Controller
                                name="invoice_zip_code"
                                control={control}
                                rules={{
                                    validate: getContactValidationRule('invoice_zip_code', requestedInvoice, invoiceRequired, validationMessages),
                                }}
                                render={({ field, fieldState }) => (
                                    <Box>
                                        <FloatingLabelInput
                                            {...field}
                                            label={t('checkout.contactInfo.invoiceZipCode')}
                                            required
                                            error={!!fieldState.error}
                                        />
                                        <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                    </Box>
                                )}
                            />
                        </Box>
                        <Controller
                            name="invoice_country"
                            control={control}
                            rules={{
                                validate: getContactValidationRule('invoice_country', requestedInvoice, invoiceRequired, validationMessages),
                            }}
                            render={({ field, fieldState }) => (
                                <Box>
                                    <FloatingLabelCountryAutocomplete
                                        value={field.value}
                                        name={field.name}
                                        inputRef={field.ref}
                                        onBlur={field.onBlur}
                                        onChange={field.onChange}
                                        label={t('checkout.contactInfo.invoiceCountry')}
                                        noOptionsText={t('checkout.contactInfo.countryNoOptions')}
                                        required
                                        error={!!fieldState.error}
                                    />
                                    <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                </Box>
                            )}
                        />
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 1,
                        }}>
                            <Controller
                                name="invoice_ic"
                                control={control}
                                rules={{
                                    validate: getContactValidationRule('invoice_ic', requestedInvoice, invoiceRequired, validationMessages),
                                }}
                                render={({ field, fieldState }) => (
                                    <Box>
                                        <FloatingLabelInput
                                            {...field}
                                            label={t('checkout.contactInfo.invoiceIc')}
                                            required={!invoiceRequired}
                                            error={!!fieldState.error}
                                        />
                                        <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                    </Box>
                                )}
                            />
                            <Controller
                                name="invoice_dic"
                                control={control}
                                rules={{
                                    validate: getContactValidationRule('invoice_dic', requestedInvoice, invoiceRequired, validationMessages),
                                }}
                                render={({ field, fieldState }) => (
                                    <Box>
                                        <FloatingLabelInput
                                            {...field}
                                            label={t('checkout.contactInfo.invoiceDic')}
                                            required={!invoiceRequired}
                                            error={!!fieldState.error}
                                        />
                                        <ValidationError message={getFieldErrorMessage(fieldState.error)} />
                                    </Box>
                                )}
                            />
                        </Box>
                    </>
                ) : null}
                {errorMessage ? (
                    <Typography level="body-sm" color="danger">
                        {errorMessage}
                    </Typography>
                ) : null}
                {!hideActions ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 1,
                    }}>
                        <Button
                            type="button"
                            variant="plain"
                            onClick={onClear}
                            disabled={requestInProgress}
                        >
                            {t('checkout.contactInfo.clear')}
                        </Button>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                        }}>
                            <Button
                                type="button"
                                variant="plain"
                                onClick={onCancel}
                                disabled={requestInProgress}
                            >
                                {t('checkout.contactInfo.cancel')}
                            </Button>
                            {/* Required by the email blur/submission contract documented above. */}
                            <Button
                                type="submit"
                                data-checkout-submission
                                variant="solid"
                                loading={requestInProgress}
                            >
                                {t('checkout.contactInfo.save')}
                            </Button>
                        </Box>
                    </Box>
                ) : null}
            </Box>
        </form>
    )
}
